import { connect } from 'react-redux';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { useField } from 'react-final-form';

import { IKnmErknmFormEvent } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormEvent';
import { IDictionaryEventTypeSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryEventTypeSelectOption';
import { DatePickerField } from 'UIPackage/MainModule/components/DatePickerField';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { parseDate } from 'CorePackage/MainModule/common/formatDate';
import { DATE_FORMAT_LONG_FNS } from 'CorePackage/MainModule/constants/dateFormats';

import { IRootState } from '../../../CorePackage/MainModule/interfaces/IRootState';
import { IInteractionCategorySelectOption } from '../../../CorePackage/CatalogsModule/interfaces/IInteractionCategorySelectOption';
import { IDecisionForm } from '../../MainModule/interfaces/IDecisionForm';
import { getDecisionFormRef } from '../../MainModule/selectors/decisionSelectors';
import { IDecisionFormRef } from '../../MainModule/interfaces/IDecisionFormRef';
import { KnmEventsDocuments } from '../../../KnmPackage/KnmEventsDocuments/components/KnmEventsDocuments';

interface IDecisionEventItemBodyProps {
  eventId: string;
  eventFieldName: string;
  eventFieldValue: IKnmErknmFormEvent;
  interactionCategoryOptions: IInteractionCategorySelectOption[] | undefined;
  isWatchOnly: boolean;
  knmEventTypesSelectOptions: IDictionaryEventTypeSelectOption[] | undefined;
  setUniqKnmEventTypesSelectOptions: Dispatch<SetStateAction<IDictionaryEventTypeSelectOption[] | undefined>>;
  sIndex: number;
  uniqKnmEventTypesSelectOptions: IDictionaryEventTypeSelectOption[] | undefined;
  decisionMinEventStartDateTime?: string;
}

interface IDecisionEventItemBodyStoreProps {
  formRef: IDecisionFormRef | undefined;
}

const DecisionEventItemBodyWithoutStore = ({
  eventId,
  eventFieldName,
  eventFieldValue,
  interactionCategoryOptions,
  isWatchOnly,
  knmEventTypesSelectOptions,
  setUniqKnmEventTypesSelectOptions,
  sIndex,
  uniqKnmEventTypesSelectOptions,
  formRef,
  decisionMinEventStartDateTime,
}: IDecisionEventItemBodyProps & IDecisionEventItemBodyStoreProps) => {
  const [isWithoutActions, setWithoutAction] = useState<boolean>(
    eventFieldValue.type?.label === 'Осуществляется без действия',
  );

  useEffect(() => {
    setWithoutAction(eventFieldValue.type?.label === 'Осуществляется без действия');
  }, [eventFieldValue.type]);

  /**
   * Уникальные типы действия
   */
  const handleSetEventType = () => {
    const events: IKnmErknmFormEvent[] | undefined = formRef?.current.getFieldState('events')?.value;

    formRef?.current.change(`${eventFieldName}.interactionCategory` as keyof IDecisionForm);
    formRef?.current.change(`${eventFieldName}.startDate` as keyof IDecisionForm);
    formRef?.current.change(`${eventFieldName}.stopDate` as keyof IDecisionForm);

    if (!events) {
      setUniqKnmEventTypesSelectOptions(knmEventTypesSelectOptions);
    } else {
      const eventsWithTypes = events.filter(e => e.type !== undefined);

      if (!eventsWithTypes) {
        setUniqKnmEventTypesSelectOptions(knmEventTypesSelectOptions);
      } else {
        const selectedTypes: IDictionaryEventTypeSelectOption[] = [];

        eventsWithTypes.forEach(x => (x.type ? selectedTypes.push(x.type) : undefined));

        let uniq = knmEventTypesSelectOptions;

        // eslint-disable-next-line no-restricted-syntax
        for (const a of selectedTypes) {
          uniq = uniq?.filter(x => x.label !== a.label);
        }

        setUniqKnmEventTypesSelectOptions(uniq);
      }
    }
  };

  const startDateEventField = useField(`${eventFieldName}.startDate`, { subscription: { value: true } });
  const startDateEventValue = startDateEventField.input.value;
  const parseStartDate = parseDate({ date: startDateEventValue || '', parseFormat: DATE_FORMAT_LONG_FNS });
  const stopDateEventField = useField(`${eventFieldName}.stopDate`, { subscription: { value: true } });
  const stopDateEventValue = stopDateEventField.input.value;

  const parseDecisionMinEventStartDateTime = useMemo(
    () => parseDate({ date: decisionMinEventStartDateTime || '', parseFormat: DATE_FORMAT_LONG_FNS }),
    [decisionMinEventStartDateTime],
  );

  return (
    <>
      <Row>
        <FieldBlock id={`${eventId}.type`}>
          <SelectInputField
            name={`${eventFieldName}.type`}
            placeholder="Выберите действие"
            options={uniqKnmEventTypesSelectOptions}
            disabled={isWatchOnly}
            size="large"
            onChange={handleSetEventType}
          />
        </FieldBlock>
      </Row>
      <Row>
        <FieldBlock id={`${eventId}.interactionCategory`}>
          <SelectInputField
            name={`${eventFieldName}.interactionCategory`}
            placeholder="Выберите категорию взаимодействия"
            options={interactionCategoryOptions}
            disabled={isWatchOnly}
            size="large"
          />
        </FieldBlock>
      </Row>
      {!isWithoutActions && (
        <>
          <Row>
            <FieldBlock id={`${eventId}.startDate`} size="half" margin="right">
              <DatePickerField
                name={`${eventFieldName}.startDate`}
                placeholder="Дата начала"
                disabled={isWatchOnly}
                minDate={parseDecisionMinEventStartDateTime}
              />
            </FieldBlock>
            <FieldBlock id={`${eventId}.stopDate`} size="half">
              <DatePickerField
                name={`${eventFieldName}.stopDate`}
                placeholder="Дата окончания"
                disabled={isWatchOnly}
                minDate={parseStartDate}
              />
            </FieldBlock>
          </Row>
        </>
      )}
      {(eventFieldValue?.type?.label || '').indexOf('Истребование документов') > -1 && (
        <KnmEventsDocuments
          sIndex={sIndex}
          formRef={formRef}
          eventFieldName={eventFieldName}
          isWatchOnly={isWatchOnly}
          isDecision
          startDateEvent={startDateEventValue}
          stopDateEvent={stopDateEventValue}
        />
      )}
    </>
  );
};

const mapStateToProps = (state: IRootState) => ({
  formRef: getDecisionFormRef(state),
});

export const DecisionEventItemBody = connect(mapStateToProps)(DecisionEventItemBodyWithoutStore);
